import { clearPlanningCache } from '../lib/planningCache';
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { environment } from '../config/environment';
import {
  AUTH_SESSION_CHANGED,
  AUTH_SESSION_REFRESH_FAILED,
  clearAuthSession,
  refreshAuthSession,
} from '../lib/authSession';
import { AppRole, Permission, getPermissionsForRole, normalizeAppRole } from '../lib/permissions';

const MAX_BROWSER_TIMEOUT = 2147483647;
const MOCK_AUTH_TOKEN_LIFETIME_MS = 60 * 60 * 1000;
const MOCK_AUTH_REFRESH_LIFETIME_MS = 7 * 24 * 60 * 60 * 1000;
const normalizedApiUrl = environment.apiUrl.replace(/\/+$/, '');

interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'publisher';
  appRole?: AppRole;
  permissions?: Permission[];
  congregation?: string;
}

export interface FirstLoginChallenge {
  challenge: 'NEW_PASSWORD_REQUIRED';
  session: string;
  username: string;
  requiredAttributes: string[];
}

interface AuthenticationPayload {
  user?: {
    id?: string;
    username?: string;
    email?: string;
    name?: string;
    role?: string;
    appRole?: string;
    permissions?: string[];
    congregation?: string;
    givenName?: string;
    familyName?: string;
  };
  token?: string;
  accessToken?: string;
  idToken?: string;
  refreshToken?: string;
  challenge?: string;
  session?: string;
  username?: string;
  requiredAttributes?: string[];
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  tokenExpiry: number | null;
  sessionError: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | {
      type: 'LOGIN_SUCCESS';
      payload: { user: User; token: string; refreshToken?: string; tokenExpiry?: number };
    }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SESSION_ERROR' };

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isLoading: true,
  isAuthenticated: false,
  tokenExpiry: null,
  sessionError: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken || null,
        tokenExpiry: action.payload.tokenExpiry || null,
        isLoading: false,
        isAuthenticated: true,
        sessionError: false,
      };
    case 'LOGIN_FAILURE':
      return { ...state, isLoading: false, isAuthenticated: false };
    case 'LOGOUT':
      return { ...initialState, isLoading: false };
    case 'SESSION_ERROR':
      return { ...state, isLoading: false, sessionError: true };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    default:
      return state;
  }
};

// JWT token utilities
const parseJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const payload = parseJWT(token);
  if (!payload || !payload.exp) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp <= currentTime;
};

const getTokenExpiry = (token: string): number | null => {
  const payload = parseJWT(token);
  return payload?.exp ? payload.exp * 1000 : null;
};

const unwrapApiPayload = <T,>(
  payload: T | { success?: boolean; data?: T } | null | undefined
): T | null => {
  if (!payload) return null;
  if (typeof payload === 'object' && 'data' in payload) {
    return (payload as { data?: T }).data ?? null;
  }
  return payload as T;
};

const encodeBase64Url = (value: string): string =>
  btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');

const createMockToken = (payload: Record<string, unknown>, expiresAt: number): string => {
  const header = encodeBase64Url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const body = encodeBase64Url(
    JSON.stringify({
      ...payload,
      exp: Math.floor(expiresAt / 1000),
      iat: Math.floor(Date.now() / 1000),
    })
  );
  return `${header}.${body}.mock-signature`;
};

const createMockSession = (email: string, name = 'Local Admin') => {
  const now = Date.now();
  const user: User = {
    id: 'mock-admin',
    email,
    name,
    role: 'admin',
    appRole: 'admin',
    permissions: getPermissionsForRole('admin'),
  };

  return {
    user,
    token: createMockToken(
      {
        sub: user.id,
        email: user.email,
        role: user.role,
        name: user.name,
        iss: 'congplanner-mock-auth',
        aud: 'congplanner-frontend',
      },
      now + MOCK_AUTH_TOKEN_LIFETIME_MS
    ),
    refreshToken: createMockToken(
      {
        sub: user.id,
        type: 'refresh',
        iss: 'congplanner-mock-auth',
        aud: 'congplanner-frontend',
      },
      now + MOCK_AUTH_REFRESH_LIFETIME_MS
    ),
  };
};

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<FirstLoginChallenge | void>;
  completeNewPassword: (
    challenge: FirstLoginChallenge,
    newPassword: string,
    userAttributes?: { given_name?: string; family_name?: string }
  ) => Promise<void>;
  logout: () => void;
  refreshTokens: () => Promise<void>;
  isTokenValid: () => boolean;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if current token is valid
  const isTokenValid = useCallback((): boolean => {
    if (!state.token) return false;
    return !isTokenExpired(state.token);
  }, [state.token]);

  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (!state.user || state.user.role !== 'admin') return false;
      const allowed = getPermissionsForRole(state.user.appRole);
      return (
        allowed.includes(permission) &&
        (!state.user.permissions || state.user.permissions.includes(permission))
      );
    },
    [state.user]
  );

  const hasAnyPermission = useCallback(
    (permissions: Permission[]): boolean => {
      return permissions.some(permission => hasPermission(permission));
    },
    [hasPermission]
  );

  // Automatic token refresh
  const refreshTokens = useCallback(async (): Promise<void> => {
    if (environment.mockAuth) {
      const storedUserData = localStorage.getItem('user_data');
      const storedUser = storedUserData ? (JSON.parse(storedUserData) as User) : null;
      const mockSession = createMockSession(
        storedUser?.email || environment.mockAuthEmail,
        storedUser?.name || 'Local Admin'
      );
      const tokenExpiry = getTokenExpiry(mockSession.token);

      localStorage.setItem('auth_token', mockSession.token);
      localStorage.setItem('refresh_token', mockSession.refreshToken);
      localStorage.setItem('user_data', JSON.stringify(mockSession.user));

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: mockSession.user,
          token: mockSession.token,
          refreshToken: mockSession.refreshToken,
          tokenExpiry: tokenExpiry || undefined,
        },
      });
      return;
    }

    await refreshAuthSession();
  }, []);

  // Restore the complete session after both context and API-client refreshes.
  useEffect(() => {
    const restoreSession = () => {
      const token = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');
      if (!token || !userData) {
        dispatch({ type: 'LOGOUT' });
        return;
      }
      try {
        const user = JSON.parse(userData) as User;
        if (!user?.id || !user.email || !user.role) {
          clearAuthSession();
          return;
        }
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user,
            token,
            refreshToken: localStorage.getItem('refresh_token') || undefined,
            tokenExpiry: getTokenExpiry(token) || undefined,
          },
        });
      } catch {
        clearAuthSession();
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (
        event.key === null ||
        ['auth_token', 'id_token', 'refresh_token', 'user_data'].includes(event.key)
      ) {
        if (event.newValue === null) clearPlanningCache();
        restoreSession();
      }
    };
    const refreshFailed = () => dispatch({ type: 'SESSION_ERROR' });
    window.addEventListener(AUTH_SESSION_REFRESH_FAILED, refreshFailed);
    window.addEventListener(AUTH_SESSION_CHANGED, restoreSession);
    window.addEventListener('storage', handleStorage);
    const token = localStorage.getItem('auth_token');
    const userData = localStorage.getItem('user_data');
    if (
      token &&
      userData &&
      (isTokenExpired(token) || !localStorage.getItem('id_token')) &&
      localStorage.getItem('refresh_token')
    ) {
      // Temporary failures retain storage and show a retry state instead of login.
      refreshTokens().catch(() => undefined);
    } else if (token && isTokenExpired(token)) {
      clearAuthSession();
    } else {
      restoreSession();
    }
    return () => {
      window.removeEventListener(AUTH_SESSION_REFRESH_FAILED, refreshFailed);
      window.removeEventListener(AUTH_SESSION_CHANGED, restoreSession);
      window.removeEventListener('storage', handleStorage);
    };
  }, [refreshTokens]);

  // Set up automatic token refresh timer
  useEffect(() => {
    if (!state.token || !state.tokenExpiry || !state.isAuthenticated) return;

    const timeUntilExpiry = state.tokenExpiry - Date.now();
    const refreshTime = Math.min(
      Math.max(timeUntilExpiry - 10 * 60 * 1000, 60 * 1000),
      MAX_BROWSER_TIMEOUT
    );

    if (refreshTime > 0) {
      const timer = setTimeout(() => {
        refreshTokens().catch(error => {
          console.error('Automatic token refresh failed:', error);
        });
      }, refreshTime);

      return () => clearTimeout(timer);
    }
  }, [state.token, state.tokenExpiry, state.isAuthenticated, refreshTokens]);

  // Set up periodic token validation
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const interval = setInterval(
      () => {
        if (state.token && isTokenExpired(state.token)) {
          refreshTokens().catch(error => {
            console.error('Periodic token refresh failed:', error);
          });
        }
      },
      5 * 60 * 1000
    ); // Check every 5 minutes

    return () => clearInterval(interval);
  }, [state.isAuthenticated, state.token, refreshTokens]);

  useEffect(() => {
    const checkSession = () => {
      if (state.isAuthenticated && state.token && isTokenExpired(state.token)) {
        refreshTokens().catch(() => undefined);
      }
    };
    window.addEventListener('focus', checkSession);
    document.addEventListener('visibilitychange', checkSession);
    return () => {
      window.removeEventListener('focus', checkSession);
      document.removeEventListener('visibilitychange', checkSession);
    };
  }, [state.isAuthenticated, state.token, refreshTokens]);

  const finishAuthentication = (data: AuthenticationPayload | null): void => {
    const token = data?.accessToken || data?.token;
    const idToken = data?.idToken;
    const newRefreshToken = data?.refreshToken;
    const user = data?.user
      ? ({
          id: data.user.id || data.user.username || data.user.email || 'unknown-user',
          email: data.user.email || '',
          name:
            data.user.name ||
            [data.user.givenName, data.user.familyName].filter(Boolean).join(' ') ||
            data.user.username ||
            data.user.email ||
            'CongPlanner User',
          role: (data.user.role === 'publisher' ? 'publisher' : 'admin') as 'publisher' | 'admin',
          appRole: normalizeAppRole(data.user.appRole),
          permissions:
            (data.user.permissions as Permission[] | undefined) ||
            getPermissionsForRole(data.user.appRole),
          congregation: data.user.congregation || '',
        } satisfies User)
      : null;

    if (!user || !token) {
      throw new Error('Invalid response from server');
    }
    if (!user.id || !user.email || !user.role) {
      throw new Error('Invalid user data received');
    }
    if (!token.includes('.') || token.split('.').length !== 3) {
      throw new Error('Invalid token format');
    }

    const tokenExpiry = getTokenExpiry(token);
    localStorage.setItem('auth_token', token);
    if (idToken) localStorage.setItem('id_token', idToken);
    localStorage.setItem('user_data', JSON.stringify(user));
    if (newRefreshToken) localStorage.setItem('refresh_token', newRefreshToken);
    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: {
        user,
        token,
        refreshToken: newRefreshToken,
        tokenExpiry: tokenExpiry || undefined,
      },
    });
  };

  const login = async (email: string, password: string): Promise<FirstLoginChallenge | void> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      if (environment.mockAuth) {
        if (
          email.trim().toLowerCase() !== environment.mockAuthEmail.toLowerCase() ||
          password !== environment.mockAuthPassword
        ) {
          throw new Error('Invalid email or password');
        }

        const mockSession = createMockSession(environment.mockAuthEmail);
        const tokenExpiry = getTokenExpiry(mockSession.token);

        localStorage.setItem('auth_token', mockSession.token);
        localStorage.setItem('refresh_token', mockSession.refreshToken);
        localStorage.setItem('user_data', JSON.stringify(mockSession.user));

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: mockSession.user,
            token: mockSession.token,
            refreshToken: mockSession.refreshToken,
            tokenExpiry: tokenExpiry || undefined,
          },
        });
        return;
      }

      const response = await fetch(`${normalizedApiUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Login failed';

        if (response.status === 401) {
          errorMessage = 'Invalid email or password';
        } else if (response.status === 429) {
          errorMessage = 'Too many login attempts. Please try again later.';
        } else if (response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (errorData.error?.message) {
          errorMessage = errorData.error.message;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }

        throw new Error(errorMessage);
      }

      const rawData = await response.json();
      const data = unwrapApiPayload<AuthenticationPayload>(rawData);
      if (data?.challenge === 'NEW_PASSWORD_REQUIRED' && data.session && data.username) {
        dispatch({ type: 'LOGIN_FAILURE' });
        return {
          challenge: 'NEW_PASSWORD_REQUIRED',
          session: data.session,
          username: data.username,
          requiredAttributes: data.requiredAttributes || [],
        };
      }
      finishAuthentication(data);
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const completeNewPassword = async (
    challenge: FirstLoginChallenge,
    newPassword: string,
    userAttributes: { given_name?: string; family_name?: string } = {}
  ): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const response = await fetch(`${normalizedApiUrl}/auth/complete-new-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: challenge.username,
          session: challenge.session,
          newPassword,
          userAttributes,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error?.message || 'Password change failed');
      }
      const data = unwrapApiPayload<AuthenticationPayload>(await response.json());
      finishAuthentication(data);
    } catch (error) {
      dispatch({ type: 'LOGIN_FAILURE' });
      throw error;
    }
  };

  const logout = useCallback(() => {
    clearAuthSession();
    dispatch({ type: 'LOGOUT' });
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    completeNewPassword,
    logout,
    refreshTokens,
    isTokenValid,
    hasPermission,
    hasAnyPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
